/**
 * The chat turn — `POST /ask` against the **local agent**, and the stream it
 * answers with. Ported from `bimgo-ai-web`'s `chatService.callAI`.
 *
 * Which server: the web client picks between api-v2 and the local agent by
 * `client_mode` (`apiUtils.getApiBaseUrl` — `revit-plugin` routes `/ask` and
 * `/abort` to the agent). This app *is* the local-agent case, so it always goes
 * to the agent, through `agentClient` so the port is resolved per request
 * (item K — the agent's port is ephemeral and a captured one goes stale).
 *
 * **The stream is NDJSON, not SSE.** One JSON object per line, no `data:`
 * prefix and no `event:` field — `res.write(JSON.stringify(...) + "\n")` in the
 * agent's `handleStreamingResponse`. Two consequences the reader has to respect:
 *
 *   1. A chunk boundary can land **mid-line**. The tail of every chunk is held
 *      back as `leftover` and prepended to the next one. Parsing chunks as if
 *      they were whole lines drops a token here and there — which looks like the
 *      model stuttering, not like a client bug, so it survives review.
 *   2. The final line may arrive without a trailing newline, so whatever is left
 *      in `leftover` when the reader finishes still has to be parsed.
 *
 * Unknown event types are **ignored, not fatal**. The agent ships separately
 * from this client (hard rule 6) and will grow events this build has never
 * heard of; throwing on one would break chat against a newer agent.
 */
import { agentClient, type AgentClient } from "./agentClient";
import { codeForStatus, parseAskError } from "./askError";
import type { Attachment } from "./attachments";
import type { HostKind } from "./hostStatus";
import { artifactFromEvent, parseToolOutput, type ExecutionResult } from "./toolResult";
import { bearer } from "./accessToken";
import { newTraceId } from "./trace";

/** `revit-plugin` is what routes `/ask` to the local agent. */
export const CLIENT_MODE = "revit-plugin";

export interface AgentStep {
  candidatePatch?: import("./skillPatch").SkillPatch;
  tool: string;
  /** The agent's id for this call, so parallel calls of one tool pair correctly; absent from older agents. */
  id?: string;
  /** `stopped` is a step the user cancelled: not finished, and not a failure. */
  status: "running" | "completed" | "error" | "stopped";
  /** What the model said this step does, when the turn asked it to; shown instead of the tool's label. */
  label?: string;
  /** What the tool returned, once it can be dug out of the payload. */
  result?: ExecutionResult;
  /** `send_code_to_revit`'s bounded artifact, only ever from a successful run. */
  artifact?: Record<string, unknown>;
  /**
   * What the user decided at an approval, kept in the flow where it happened
   * — the old web's decided `HitlApprovalSection` (item BS). A step with a
   * decision never ran anything: `tool` is the gated action's name.
   */
  decision?: StepDecision;
  /**
   * What the model said just before this call, when the caller takes it out of
   * the answer (`AskCallbacks.onTextToStep`). Shown above the row, so narration
   * stays where it was said instead of piling up under every step.
   */
  note?: string;
  /**
   * Images the tool returned, as URLs ready to show. Only a reopened AutoCAD skill
   * conversation has them: its Work Journal kept them (`lib/skillEditJournal.ts`).
   */
  images?: string[];
}

export type DecisionKind = "approved" | "rejected" | "revised" | "answered";

export interface StepDecision {
  kind: DecisionKind;
  answer?: string;
  /** The action's own title (a skill's display name, the code's name), or empty. */
  title: string;
  /** i18n key rendered when `title` is empty. */
  fallbackKey: string;
}

/**
 * How much of the model's context window this thread now occupies.
 *
 * `tokens` is the provider's own reported prompt size, sent on the `usage`
 * event at the end of every turn. `window` is the model's limit, or null when
 * the plan publishes none — and with no denominator there is no percentage, so
 * the meter stays hidden rather than guessing. A local `chars ÷ 4` estimate
 * under-counts Traditional Chinese by ~3.5x, which would invert the warning for
 * exactly the users this ships for.
 */
export interface ContextUsage {
  tokens: number;
  window: number | null;
}

/** Percentages at which the context meter changes appearance. */
export const CONTEXT_USAGE_TIERS = { notice: 50, warn: 80, critical: 90 } as const;

export type ContextUsageLevel = "low" | "notice" | "warn" | "critical";

/**
 * 0–100, or null when there is nothing honest to show.
 *
 * A zero reading is *not* a reading, and this returns null for it as the
 * reference does. The agent only emits `usage` once a call has been billed, so
 * zero tokens against a real window means the count did not arrive rather than
 * that the window is empty — and "0%" is a far more confident claim than "no
 * reading yet".
 */
export function contextUsagePercent(usage: ContextUsage | null): number | null {
  if (!usage || !usage.window || usage.window <= 0) return null;
  if (!Number.isFinite(usage.tokens) || usage.tokens <= 0) return null;
  return Math.min(100, Math.round((usage.tokens / usage.window) * 100));
}

/**
 * Pair the last turn's reading with the model the picker shows **now**.
 *
 * Ported from `bimgo-ai-web`'s `resolveContextUsage`, and the direction is the
 * whole point: the active model's window *replaces* the one the stream reported,
 * rather than merely filling in for a missing one. Escaping a full window is the
 * main reason anyone switches model, so the confirmation that it worked has to
 * land on the switch — not at the end of the next turn. A meter still measured
 * against the model you just left reads 92% on a window three times the size.
 *
 * The token count carries over unchanged until the next turn re-counts it. It
 * was produced by the previous model's tokenizer, so the first reading after a
 * switch is close rather than exact — which is the right trade against showing
 * a number that is wrong by a multiple.
 *
 * A null `modelWindow` leaves the reading alone: the picker knowing no window is
 * not evidence that the stream's was wrong.
 */
export function resolveContextUsage(
  reading: ContextUsage | null,
  modelWindow: number | null,
): ContextUsage | null {
  if (!reading) return null;
  if (modelWindow === null) return reading;
  return { tokens: reading.tokens, window: modelWindow };
}

export function contextUsageLevel(percent: number): ContextUsageLevel {
  if (percent >= CONTEXT_USAGE_TIERS.critical) return "critical";
  if (percent >= CONTEXT_USAGE_TIERS.warn) return "warn";
  if (percent >= CONTEXT_USAGE_TIERS.notice) return "notice";
  return "low";
}

/** One action the agent is asking permission to take (HITL). */
export interface ActionRequest {
  name: string;
  args?: Record<string, unknown>;
  description?: string;
}

export interface ReviewConfig {
  actionName: string;
  allowedDecisions: ("approve" | "edit" | "reject")[];
}

export interface InterruptPayload {
  thread_id: string;
  action_requests: ActionRequest[];
  review_configs: ReviewConfig[];
}

/** What the user decided about an interrupt, sent back on the next `/ask`. */
export interface ResumeDecision {
  approved: boolean;
  /** Plan mode only: re-plan with edits, or stop planning. */
  intent?: "revise" | "reject";
  edited_plan?: string;
  edited_title?: string;
  /** `ask_user`: the answer to the agent's question. */
  answer?: string;
  decision_reason?: string;
}

/**
 * Which endpoint runs the turn. Chat is `POST /ask`; a workflow's conversation
 * posts to that workflow's own routes — `POST /workflows/:id/threads` to start
 * one, `POST /workflows/:id/threads/:threadId/turns` for every later turn
 * (`docs/proposals/workflows.md`, "Agent routes"). The body and the stream are
 * the same either way; only the URL differs, so the Conversation tab inside a
 * workflow's view is this same transport pointed elsewhere.
 */
export type AskRoute = { kind: "chat" } | { kind: "workflow"; workflowId: string; start: boolean };

/** The path a route resolves to. A start keeps the start path on a retry that already has a thread. */
export function askPath(route: AskRoute | undefined, threadId: string | null): string {
  if (!route || route.kind === "chat") return "/ask";
  const threads = `/workflows/${encodeURIComponent(route.workflowId)}/threads`;
  return route.start || !threadId ? threads : `${threads}/${encodeURIComponent(threadId)}/turns`;
}

export interface AskParams {
  /**
   * A workflow section's own request fields (`workflows/`), sent beside the
   * message on a new turn and never on a resume. Opaque here: the workflow routes
   * read them (`work_settings`, `end_report_submission`). Never overrides a field
   * this module sets.
   */
  extraBody?: Record<string, unknown>;
  /** Where the turn goes; absent means chat (`/ask`). */
  route?: AskRoute;
  userInput: string;
  projectId: string;
  organizationId: string;
  userId: string;
  threadId: string | null;
  accessToken: string;
  systemInput?: string | null;
  agentMode?: string;
  /** Both or neither: the agent falls back to its own default unless it has a
   *  provider *and* a model, and 400s on a model the org's plan does not expose. */
  llmProvider?: string;
  llmModel?: string;
  /**
   * The composer's reasoning chip (#111). Omitted, the agent runs at its own
   * default; the agent 400s on a value outside its four names, so the chip is
   * the only thing that ever sets this.
   */
  reasoningEffort?: string;
  /** Image / PDF / Excel already uploaded to S3 — URLs only, never bytes. A DWG is a local path and is sent separately. */
  attachments?: Attachment[];
  /**
   * Which CAD instance this turn addresses — the plugin's own `sessionId`.
   *
   * One selection app-wide, made in the instance card (`selectedInstance.ts`) and
   * shared with skill execution and skill editing. The agent resolves it against
   * the published manifests and **refuses rather than substitutes** when it is
   * gone, so a stale id fails loudly instead of driving a document the user did
   * not choose.
   */
  targetSessionId?: string | null;
  /**
   * One id per connected vendor (#5) — `{ revit, autocad }`. The agent builds
   * the turn's tool set from every one and routes each tool call to its
   * vendor's instance. Sent alongside `targetSessionId`, which an older agent
   * still reads; a newer one prefers this.
   */
  targetSessions?: Partial<Record<HostKind, string>>;
  resume?: ResumeDecision;
  /**
   * Continue a cut-off workflow run from the agent's saved checkpoint: no new
   * message, the pending step runs again (`docs/proposals/workflows.md`, "TODO:
   * stuck turns and resume"). Workflow routes with a thread only. `confirmDestructive`
   * is the user's word that a tool which may already have changed the model can run again.
   */
  continue?: { confirmDestructive: boolean };
  abortSignal?: AbortSignal;
}

export interface AskCallbacks {
  onThreadInfo: (threadId: string) => void;
  onToken: (content: string) => void;
  onReasoning: (content: string) => void;
  onSteps: (steps: AgentStep[]) => void;
  /**
   * Text streamed since the previous step, now attached to the step that
   * follows it as `note`. The caller removes it from the answer. Without this
   * callback the text stays in the answer and steps carry no note.
   */
  onTextToStep?: (text: string) => void;
  onInterrupt: (payload: InterruptPayload) => void;
  onContextUsage: (usage: ContextUsage) => void;
  /**
   * A terminal `error` event. The stream is cancelled after this fires.
   * `traceId` is the turn's own — the one sent as `X-Trace-Id` — so the card
   * can show it as the reference to quote to support.
   *
   * `details` is the agent's structured half (`StreamErrorEvent.details`). It
   * carries the billing gate's `reason` on a 402, which is what tells "no
   * subscription" from "out of credits" — dropping it here is what made all four
   * blocks read as one sentence (#64).
   */
  onStreamError: (
    code: string,
    message: string,
    traceId?: string,
    details?: Record<string, unknown>,
  ) => void;
  /**
   * The turn was cancelled rather than finished.
   *
   * Fires for a local Stop **and** for an abort the agent began, and it fires
   * exactly once. Without it the client had no way to say anything: an aborted
   * turn resolved silently, so pressing Stop left a half-written answer and no
   * indication that stopping was what ended it — indistinguishable from the
   * model simply trailing off.
   */
  onAborted?: () => void;
  /**
   * Every parsed event, before this module interprets it. The skill editor
   * watches `commit_skill_version`'s `tool_end` here — a save it must react to
   * that the generic step list deliberately flattens.
   */
  onRawEvent?: (data: Record<string, unknown>) => void;
}

/**
 * Thrown when `/ask` itself is refused, before any streaming begins.
 *
 * `code` is always one of the taxonomy's, never null: the failures a session
 * meets need different remedies, and a caller that has to guess from a status
 * cannot offer one.
 *
 * `traceId` is the id this turn was sent under (`X-Trace-Id`). The error card
 * shows it as `Reference: …`; the agent logged the refusal under the same id,
 * so the reference the user quotes is the key that finds it.
 */
export class AskError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  readonly traceId: string | undefined;
  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
    traceId?: string,
  ) {
    super(message);
    this.name = "AskError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.traceId = traceId;
  }
}

/**
 * The tool events that carry a `tool` name and move a step through its states.
 * Skill activity (`read_skill_*`, `execute_skill_*`, `search_skill_*`) is folded
 * into the same step list rather than given its own panel — the skills *view* is
 * a separate feature; what belongs in the transcript is that the agent is doing
 * something and what it is.
 */
const SKILL_START: Record<string, string> = {
  read_skill_start: "read_skill",
  execute_skill_start: "execute_skill",
  search_skill_start: "search_skill",
};
const SKILL_END: Record<string, string> = {
  read_skill_end: "read_skill",
  execute_skill_end: "execute_skill",
  search_skill_end: "search_skill",
};

/** Cloud attachments stay on the upload path. DWGs leave as local paths only. */
function partitionAttachments(list: readonly Attachment[]): {
  cloud: Attachment[];
  drawings: { filename: string; path: string }[];
} {
  const cloud: Attachment[] = [];
  const drawings: { filename: string; path: string }[] = [];
  for (const item of list) {
    if (item.kind === "dwg" && item.localPath) {
      drawings.push({ filename: item.filename, path: item.localPath });
    } else if (item.kind !== "dwg") {
      cloud.push(item);
    }
  }
  return { cloud, drawings };
}

/**
 * Run one turn. Resolves when the stream ends; rejects on a refused request or
 * a transport failure. An aborted turn resolves rather than rejecting — the user
 * stopping a turn is not an error.
 */
export async function ask(
  params: AskParams,
  callbacks: AskCallbacks,
  deps: { client?: AgentClient } = {},
): Promise<void> {
  const body: Record<string, unknown> = {
    system_input: params.systemInput ?? null,
    project_id: params.projectId,
    organization_id: params.organizationId,
    user_id: params.userId,
    thread_id: params.threadId,
    client_mode: CLIENT_MODE,
    ...(params.agentMode ? { agent_mode: params.agentMode } : {}),
    ...(params.llmProvider && params.llmModel
      ? { llm_provider: params.llmProvider, llm_model: params.llmModel }
      : {}),
    ...(params.reasoningEffort ? { reasoning_effort: params.reasoningEffort } : {}),
  };
  const parted = partitionAttachments(params.attachments ?? []);
  // Omitted entirely when there are none: the agent's validator promotes a
  // legacy `images` array into `attachments`, and sending an empty one of
  // either would look like a deliberate "no attachments" override.
  if (parted.cloud.length > 0) body["attachments"] = parted.cloud;
  if (parted.drawings.length > 0) body["baseline_drawings"] = parted.drawings;
  // Omitted when nothing is bound rather than sent as null: the agent reads an
  // absent target as "none named" and a present one as "this exact instance",
  // and those are different requests.
  if (params.targetSessionId) body["target_session_id"] = params.targetSessionId;
  if (params.targetSessions && Object.keys(params.targetSessions).length > 0) {
    body["target_sessions"] = params.targetSessions;
  }
  // Exactly one of the three: the agent rejects a resume or a continue with no
  // thread, a continue beside either of the others, and a turn with none of them.
  if (params.continue) {
    body["continue"] = true;
    if (params.continue.confirmDestructive) body["confirm_destructive"] = true;
  } else if (params.resume) body["resume"] = params.resume;
  else {
    body["user_input"] = params.userInput;
    for (const [key, value] of Object.entries(params.extraBody ?? {})) {
      if (!(key in body)) body[key] = value;
    }
  }

  return streamAsk(
    {
      path: askPath(params.route, params.threadId),
      body,
      accessToken: params.accessToken,
      abortSignal: params.abortSignal,
      threadIdFallback: params.threadId,
      organizationId: params.organizationId,
    },
    callbacks,
    deps,
  );
}

/** One streamed turn against any `/ask`-shaped endpoint — the reader itself. */
export interface StreamAskRequest {
  path: string;
  body: Record<string, unknown>;
  accessToken: string;
  abortSignal?: AbortSignal | undefined;
  /** What `interrupt` events fall back to when they carry no thread id. */
  threadIdFallback?: string | null | undefined;
  /** Sent as `X-Organization-Id` when present — the gated routes need it. */
  organizationId?: string | undefined;
  /**
   * The turn's trace id, sent as `X-Trace-Id`. Minted here when absent — one
   * per request, never reused across turns, so a reference names exactly one.
   */
  traceId?: string | undefined;
}

export async function streamAsk(
  request: StreamAskRequest,
  callbacks: AskCallbacks,
  deps: { client?: AgentClient } = {},
): Promise<void> {
  const client = deps.client ?? agentClient;
  // One id for the whole turn: the header, every error this function raises for
  // it, and the terminal `error` event all carry the same value.
  const traceId = request.traceId ?? newTraceId();

  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: bearer(request.accessToken),
      "X-Trace-Id": traceId,
      ...(request.organizationId ? { "X-Organization-Id": request.organizationId } : {}),
    },
    body: JSON.stringify(request.body),
  };
  if (request.abortSignal) init.signal = request.abortSignal;

  let response: Response;
  try {
    response = await client.fetch(request.path, init);
  } catch (error) {
    // Stopped before the agent answered at all: there is no stream to read and
    // no partial reply, so this is the only chance to say the turn was cancelled.
    if (isAbort(error)) {
      callbacks.onAborted?.();
      return;
    }
    // The agent is a local process: a throw here is it being down or gone, not
    // a bad request. Saying so beats a raw TypeError from `fetch`.
    throw new AskError(0, "SERVICE_UNAVAILABLE", String(error), undefined, traceId);
  }

  if (!response.ok) {
    const parsed = parseAskError(
      response.status,
      await response.json().catch(() => null),
      response.statusText,
    );
    throw new AskError(parsed.httpStatus, parsed.code, parsed.message, parsed.details, traceId);
  }
  if (!response.body) {
    throw new AskError(response.status, codeForStatus(response.status), "", undefined, traceId);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const steps: AgentStep[] = [];
  // Tokens since the last step, so they can move onto the next one.
  let sinceLastStep = "";
  let leftover = "";
  let stopped = false;
  // The turn was cancelled rather than finished — set from the agent's own
  // `usage.outcome`, since the local signal only covers aborts this client began.
  let cancelled = false;

  /** The running step an event belongs to: by id, or by tool name from an agent that sends none. */
  const runningStep = (data: Record<string, unknown>, tool: string): number => {
    const id = typeof data["id"] === "string" ? data["id"] : null;
    return steps.findIndex((s) => s.status === "running" && (id ? s.id === id : s.tool === tool));
  };

  /** Move the running step an event belongs to into a terminal state. */
  const finish = (
    data: Record<string, unknown>,
    tool: string,
    status: "completed" | "error",
    extra?: Omit<Partial<AgentStep>, "tool" | "status">,
  ) => {
    const index = runningStep(data, tool);
    if (index === -1) return;
    steps[index] = { ...steps[index], tool, status, ...(extra ?? {}) };
    callbacks.onSteps([...steps]);
  };

  /** Attach a result to a running step without ending it — `tool_artifact`. */
  const annotate = (data: Record<string, unknown>, tool: string, extra: Omit<Partial<AgentStep>, "tool" | "status">) => {
    const index = runningStep(data, tool);
    if (index === -1) return;
    steps[index] = { ...steps[index], tool, status: "running", ...extra };
    callbacks.onSteps([...steps]);
  };

  /** Start a step, carrying what the model said just before it. */
  const start = (step: AgentStep) => {
    const note = sinceLastStep.trim();
    if (callbacks.onTextToStep && sinceLastStep) {
      callbacks.onTextToStep(sinceLastStep);
      if (note) step = { ...step, note };
    }
    sinceLastStep = "";
    steps.push(step);
    callbacks.onSteps([...steps]);
  };

  const handle = (data: Record<string, unknown>): void => {
    callbacks.onRawEvent?.(data);
    const type = data["type"];
    switch (type) {
      case "thread_info": {
        const id = data["thread_id"];
        if (typeof id === "string" && id) callbacks.onThreadInfo(id);
        return;
      }
      case "token": {
        if (typeof data["content"] === "string") {
          sinceLastStep += data["content"];
          callbacks.onToken(data["content"]);
        }
        return;
      }
      case "reasoning_token": {
        if (typeof data["content"] === "string") callbacks.onReasoning(data["content"]);
        return;
      }
      case "tool_start": {
        const tool = data["tool"];
        if (typeof tool !== "string") return;
        const id = typeof data["id"] === "string" ? data["id"] : "";
        const label = typeof data["displayName"] === "string" ? data["displayName"].trim() : "";
        start({ tool, status: "running", ...(id ? { id } : {}), ...(label ? { label } : {}) });
        return;
      }
      case "tool_end": {
        if (typeof data["tool"] !== "string") return;
        const parsed = parseToolOutput(data["output"]);
        // A tool that reported failure inside its own payload did not succeed,
        // whatever the event name says — the stream sends `tool_end` either way.
        const status = parsed.execution && !parsed.execution.success ? "error" : "completed";
        finish(data, data["tool"], status, {
          ...(parsed.execution ? { result: parsed.execution } : {}),
          ...(parsed.artifact ? { artifact: parsed.artifact } : {}),
        });
        return;
      }
      case "tool_artifact": {
        // Emitted *before* `tool_end`, carrying a bounded copy that survives
        // the stream normalising an oversized result away.
        if (typeof data["tool"] !== "string") return;
        const parsed = artifactFromEvent(data);
        if (!parsed.artifact) return;
        annotate(data, data["tool"], {
          artifact: parsed.artifact,
          ...(parsed.execution ? { result: parsed.execution } : {}),
        });
        return;
      }
      case "tool_error": {
        if (typeof data["tool"] === "string") finish(data, data["tool"], "error");
        return;
      }
      case "interrupt": {
        callbacks.onInterrupt({
          thread_id:
            typeof data["thread_id"] === "string" ? data["thread_id"] : (request.threadIdFallback ?? ""),
          action_requests: Array.isArray(data["action_requests"])
            ? (data["action_requests"] as ActionRequest[])
            : [],
          review_configs: Array.isArray(data["review_configs"])
            ? (data["review_configs"] as ReviewConfig[])
            : [],
        });
        return;
      }
      case "usage": {
        // How the turn ended, as the agent saw it. The client usually knows
        // already — it pressed Stop — but not always: the agent aborts a turn of
        // its own accord when the socket drops or a second turn takes the thread,
        // and then the stream simply ends and nothing local says why. Without
        // this, those steps settle as "completed" and claim work that stopped.
        if (data["outcome"] === "aborted") cancelled = true;

        // The only place the client learns how full the window is; billing is
        // the agent's job and happens server-side off this same event.
        const tokens = data["context_tokens"];
        if (typeof tokens !== "number") return;
        const window = data["context_window"];
        callbacks.onContextUsage({
          tokens,
          window: typeof window === "number" ? window : null,
        });
        return;
      }
      case "error": {
        const code = typeof data["code"] === "string" ? data["code"] : "INTERNAL_ERROR";
        const message =
          typeof data["message"] === "string" ? data["message"] : "the agent reported an error";
        const raw = data["details"];
        const details =
          typeof raw === "object" && raw !== null && !Array.isArray(raw)
            ? (raw as Record<string, unknown>)
            : undefined;
        callbacks.onStreamError(code, message, traceId, details);
        stopped = true;
        return;
      }
      default: {
        if (typeof type !== "string") return;
        const started = SKILL_START[type];
        if (started) {
          start({ tool: started, status: "running" });
          return;
        }
        const ended = SKILL_END[type];
        if (ended) {
          finish({}, ended, "completed");
          return;
        }
        // Anything else is from a newer agent than this build. Ignored.
        return;
      }
    }
  };

  const consume = (line: string): void => {
    if (!line.trim()) return;
    let data: unknown;
    try {
      data = JSON.parse(line);
    } catch {
      // A malformed line must not take the turn down; the rest of the stream is
      // still worth rendering.
      return;
    }
    if (typeof data === "object" && data !== null) handle(data as Record<string, unknown>);
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = leftover + decoder.decode(value, { stream: true });
      const lines = chunk.split("\n");
      // The last piece may be half a line; hold it for the next chunk.
      leftover = lines.pop() ?? "";
      for (const line of lines) {
        consume(line);
        if (stopped) break;
      }
      if (stopped) break;
    }
    if (!stopped) consume(leftover);
  } catch (error) {
    if (!isAbort(error)) throw error;
  } finally {
    if (stopped) await reader.cancel().catch(() => undefined);
  }

  // A stream that ends while steps are still running leaves them spinning
  // forever. How they are settled depends on *why* the stream ended — see
  // `settleSteps`.
  const wasCancelled = cancelled || request.abortSignal?.aborted === true;
  const settled = settleSteps(steps, wasCancelled);
  if (settled.some((s, i) => s.status !== steps[i]?.status)) {
    callbacks.onSteps(settled);
  }
  // After the steps, so the transcript settles before it is annotated.
  if (wasCancelled) callbacks.onAborted?.();
}

/**
 * Close off steps left running when the stream ended.
 *
 * **A stopped turn did not complete its work, and must not say it did.** The
 * first version marked every running step `completed` whatever ended the stream,
 * so pressing Stop while `send_code_to_revit` was in flight left a green tick
 * and a finished row behind — the UI reporting success for the work the user had
 * just cancelled, about the most misleading thing it could say. `bimgo-ai-web`
 * has the same flaw; it is not worth reproducing.
 *
 * A stream that ends *normally* with a step still running is a different case:
 * the graph does not finish while a tool is in flight, so a missing `tool_end`
 * there means the event was lost rather than the work. `completed` remains the
 * honest reading of it.
 */
export function settleSteps(steps: readonly AgentStep[], aborted: boolean): AgentStep[] {
  const ending: AgentStep["status"] = aborted ? "stopped" : "completed";
  return steps.map((step) => (step.status === "running" ? { ...step, status: ending } : step));
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/**
 * Ask the agent to stop a turn.
 *
 * Aborting the `fetch` alone stops *this* client reading, but leaves the agent
 * running the turn — burning credits and, worse, still driving Revit. The agent
 * keeps an `AbortController` per thread for exactly this call.
 */
export async function abortTurn(
  threadId: string,
  accessToken: string,
  deps: { client?: AgentClient } = {},
): Promise<void> {
  const client = deps.client ?? agentClient;
  await client.fetch("/abort", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: bearer(accessToken),
    },
    body: JSON.stringify({ thread_id: threadId }),
  });
}
